import { apiGet, apiPatch, apiDelete } from '../api.ts'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { QueryCacheKeys } from '@/api/queryKeys.ts'
import { createUrl } from '@/util/url.ts'
import { MINUTE } from '@/constant/time.ts'

/** 설정 데이터 응답 타입 */
export interface IResGetSettings {
    id: string;
    contact: string;
    address: string;
    latitude: number;
    longitude: number;
    deviceSerial: string;
    safeZoneRadiusM: number;
    isAppPush: boolean;
}

/** 기본값을 설정하여 undefined 반환 방지 */
const DEFAULT_SETTINGS: IResGetSettings = {
    id: '',
    contact: '',
    address: '',
    latitude: 0,
    longitude: 0,
    deviceSerial: '',
    safeZoneRadiusM: 100, // 100m 기본값
    isAppPush: true,
};

/** GET 요청: 설정 데이터 가져오기 */
export const apiGetSettings = async (): Promise<IResGetSettings> => {
    try {
        const { data } = await apiGet<IResGetSettings>(createUrl(`/app/auth/setting`, {}));
        return { ...DEFAULT_SETTINGS, ...data }; // 기본값과 병합하여 반환
    } catch (error) {
        console.error("API 요청 실패:", error);
        return DEFAULT_SETTINGS; // 요청 실패 시 기본값 반환
    }
};

/** PATCH 요청: 디바이스 시리얼 업데이트 (별도 엔드포인트 사용) */
export const apiPatchDeviceSerial = async (deviceSerial: string) => {
    try {
        const response = await apiPatch(createUrl(`/app/auth/setting/device`, {}), { deviceSerial });
        
        // API가 성공을 반환했는지 확인
        if (response.code === 200) {
            return response.data;
        } else {
            // API에서 명시적인 오류 코드와 메시지 전달
            throw {
                response: {
                    status: response.code,
                    data: {
                        code: response.code,
                        message: response.message || "이미 등록된 시리얼입니다."
                    }
                }
            };
        }
    } catch (error) {
        console.error("디바이스 시리얼 업데이트 실패:", error);
        throw error;
    }
};

/** PATCH 요청: 설정 데이터 업데이트 (기존 설정 전체) */
export const apiPatchSettings = async (updateData: Partial<IResGetSettings>, currentData: IResGetSettings) => {
    try {
        const updatedSettings = {
            ...currentData, // 기존 데이터 유지
            ...updateData,  // 변경된 값만 덮어쓰기
        };

        const { data } = await apiPatch(createUrl(`/app/auth/setting`, {}), updatedSettings);
        return data;
    } catch (error) {
        console.error("설정 업데이트 실패:", error);
        throw error;
    }
};

/** DELETE 요청: 회원탈퇴 */
export const apiDeleteUser = async (): Promise<any> => {
    try {
        const { data } = await apiDelete(createUrl(`/app/auth/user`, {}));
        return data;
    } catch (error) {
        console.error("회원탈퇴 요청 실패:", error);
        throw error;
    }
};

/** React Query Hook */
const useQueryGetSettings = () => {
    return useQuery({
        queryKey: QueryCacheKeys.settings.getSettings(),
        queryFn: apiGetSettings,
        refetchInterval: MINUTE, // 1분마다 자동 갱신
    });
};

/** React Query Mutation Hook (PATCH 요청 - 전체 설정) */
export const useMutationUpdateSettings = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (updateData: Partial<IResGetSettings>) => {
            const currentData = queryClient.getQueryData<IResGetSettings>(QueryCacheKeys.settings.getSettings()) || DEFAULT_SETTINGS;
            return apiPatchSettings(updateData, currentData);
        },
        onSuccess: (data) => {
            queryClient.setQueryData(QueryCacheKeys.settings.getSettings(), data);
        },
    });
};

/** React Query Mutation Hook (PATCH 요청 - 디바이스 시리얼만 변경) */
export const useMutationUpdateDeviceSerial = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (deviceSerial: string) => {
            return apiPatchDeviceSerial(deviceSerial);
        },
        onSuccess: () => {
            // console.log("디바이스 시리얼이 성공적으로 업데이트됨", data);
            queryClient.invalidateQueries({ queryKey: QueryCacheKeys.settings.getSettings() });
        },
        onError: (error: any) => {
            console.error("디바이스 시리얼 업데이트 실패:", error);
            
            if (
                error?.response?.status === 400 || 
                error?.response?.data?.code === 400 ||
                error?.response?.data?.message?.includes("이미 등록된")
            ) {
                console.warn("이미 등록된 디바이스 시리얼입니다:", error?.response?.data?.message);
            }
        }
    });
};

/** React Query Mutation Hook (DELETE 요청 - 회원탈퇴) */
export const useMutationDeleteUser = () => {
    return useMutation({
        mutationFn: apiDeleteUser,
    });
};

export default useQueryGetSettings;
